import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import { ApiError } from "../lib/api";
import { Masthead } from "../components/Masthead";
import { IconSpinner } from "../components/Icons";

interface LocationState {
  from?: { pathname: string };
}

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) return null;
  if (user) {
    const dest = user.role === "client" ? "/c/me" : "/admin";
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      const redirectTo =
        (location.state as LocationState | null)?.from?.pathname ??
        (u.role === "client" ? "/c/me" : "/admin");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to sign in");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-narrow">
      <Masthead as="div" />

      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 400,
          fontSize: "var(--text-xl)",
          color: "var(--ink)",
          marginBottom: "var(--space-4)",
          marginTop: "var(--space-2)",
        }}
      >
        Sign in to continue
      </h2>

      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            spellCheck={false}
            inputMode="email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting && <IconSpinner size={14} />}
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>

      <p className="disclaim-line">
        <strong>This is not an appraisal.</strong> It is a valuation analysis for commercial real estate.
      </p>
    </main>
  );
}
